import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryProvider } from '@/app/providers/query-provider';
import { AuthProvider } from '@/app/providers/auth-provider';
import { DashboardLayout, AuthLayout } from '@/app/layout';
import { LoginPage } from '@/features/auth/components/login-page';
import { EmailLinkComplete } from '@/features/auth/components/email-link-complete';
import { RegisterPage } from '@/features/auth/components/register-page';
import { DashboardPage } from '@/features/dashboard/components/dashboard-page';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { logAnalyticsEvent } from '@/lib/firebase';

// Lazy load feature pages
const MerchantsPage = React.lazy(() => 
  import('@/features/merchants/components/merchants-page')
);
const PlansPage = React.lazy(() =>
  import('@/features/plans/components/plans-page')
);
const PromotionsPage = React.lazy(() =>
  import('@/features/plans/components/promotions-page')
);
const LocationsPage = React.lazy(() =>
  import('@/features/locations/components/locations-page')
);
const WaitlistsPage = React.lazy(() =>
  import('@/features/waitlists/components/waitlists-page')
);
const AnalyticsPage = React.lazy(() =>
  import('@/features/analytics/components/analytics-page')
);
const SettingsPage = React.lazy(() =>
  import('@/features/settings/components/settings-page')
);
const UsersPage = React.lazy(() =>
  import('@/features/users/components/users-page')
);
const AffiliatesPage = React.lazy(() =>
  import('@/features/affiliates/components/affiliates-page')
);
const AffiliateLandingPage = React.lazy(() =>
  import('@/features/affiliates/components/affiliate-landing')
);
const MerchantSignupFormsPage = React.lazy(() =>
  import('@/features/partners/components/merchant-signup-forms-page')
);
// Applications feature removed - approvals now handled in merchants table
const MerchantSignup = React.lazy(() =>
  import('@/features/partners/components/merchant-signup-simple')
);
const SignupProcessing = React.lazy(() =>
  import('@/features/partners/components/signup-processing')
);
const MerchantSignupExisting = React.lazy(() =>
  import('@/features/partners/components/merchant-signup-existing')
);
const MerchantConversion = React.lazy(() =>
  import('@/features/partners/components/merchant-conversion')
);
const MerchantSignupSuccess = React.lazy(() =>
  import('@/pages/partners/merchant-signup-success')
);
const MerchantOnboardingDashboard = React.lazy(() =>
  import('@/features/merchants/components/merchant-onboarding-dashboard')
);
const BusinessProfileSetup = React.lazy(() =>
  import('@/features/merchants/components/onboarding/business-profile-setup')
);
const LocationSetup = React.lazy(() =>
  import('@/features/merchants/components/onboarding/location-setup')
);
const SettingsSetup = React.lazy(() =>
  import('@/features/merchants/components/onboarding/settings-setup')
);
const ApplicationSubmitted = React.lazy(() =>
  import('@/features/merchants/components/onboarding/application-submitted')
);
const BillingPage = React.lazy(() =>
  import('@/pages/dashboard/billing')
);
const BrandingPage = React.lazy(() =>
  import('@/pages/dashboard/branding')
);
const RedemptionsPage = React.lazy(() =>
  import('@/features/redemptions/components/redemptions-page')
);
const TermsAndConditionsPage = React.lazy(() =>
  import('@/pages/legal/terms-and-conditions')
);
const PrivacyPolicyPage = React.lazy(() =>
  import('@/pages/legal/privacy-policy')
);
const HomePage = React.lazy(() => import('@/pages/Home'));
const PartnersMarketingPage = React.lazy(() => import('@/pages/Partners'));

function RouteAnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    void logAnalyticsEvent('page_view', {
      path: location.pathname,
      search: location.search,
    });
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <QueryProvider>
        <AuthProvider>
          <RouteAnalyticsTracker />
          <Routes>
            <Route
              path="/"
              element={<MarketingLayout />}
            >
              <Route
                index
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <HomePage />
                  </React.Suspense>
                }
              />
              <Route
                path="partners"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PartnersMarketingPage />
                  </React.Suspense>
                }
              />
            </Route>

            {/* Auth routes */}
            <Route path="/auth" element={<AuthLayout />}>
              <Route path="login" element={<LoginPage />} />
              <Route path="register" element={<RegisterPage />} />
              <Route path="complete-signin" element={<EmailLinkComplete />} />
              <Route index element={<Navigate to="/auth/login" replace />} />
            </Route>

            {/* Protected dashboard routes */}
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<DashboardPage />} />
            </Route>

            {/* Merchant Management */}
            <Route path="/merchants" element={<DashboardLayout />}>
              <Route 
                index 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <MerchantsPage />
                  </React.Suspense>
                } 
              />
              <Route 
                path="onboarding" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <MerchantOnboardingDashboard />
                  </React.Suspense>
                } 
              />
              <Route 
                path="onboarding/business" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <BusinessProfileSetup />
                  </React.Suspense>
                } 
              />
              <Route 
                path="onboarding/locations" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <LocationSetup />
                  </React.Suspense>
                } 
              />
              <Route 
                path="onboarding/settings" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <SettingsSetup />
                  </React.Suspense>
                } 
              />
              <Route 
                path="application-submitted" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ApplicationSubmitted />
                  </React.Suspense>
                } 
              />
            </Route>

            {/* Context-aware pages */}
            <Route path="/locations" element={<DashboardLayout />}>
              <Route 
                index 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <LocationsPage />
                  </React.Suspense>
                } 
              />
            </Route>

            <Route path="/waitlists" element={<DashboardLayout />}>
              <Route 
                index 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <WaitlistsPage />
                  </React.Suspense>
                } 
              />
            </Route>

            <Route path="/plans" element={<DashboardLayout />}>
              <Route 
                index 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PlansPage />
                  </React.Suspense>
                } 
              />
              <Route
                path="promotions"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PromotionsPage />
                  </React.Suspense>
                }
              />
            </Route>

            <Route path="/analytics" element={<DashboardLayout />}>
              <Route 
                index 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <AnalyticsPage />
                  </React.Suspense>
                } 
              />
            </Route>

            <Route path="/redemptions" element={<DashboardLayout />}>
              <Route 
                index 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <RedemptionsPage />
                  </React.Suspense>
                } 
              />
            </Route>

            <Route path="/settings" element={<DashboardLayout />}>
              <Route 
                index 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <SettingsPage />
                  </React.Suspense>
                } 
              />
              <Route 
                path="billing" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <BillingPage />
                  </React.Suspense>
                } 
              />
              <Route 
                path="branding" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <BrandingPage />
                  </React.Suspense>
                } 
              />
            </Route>

            {/* Other pages */}

            <Route path="/merchant-signup-forms" element={<DashboardLayout />}>
              <Route
                index
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <MerchantSignupFormsPage />
                  </React.Suspense>
                }
              />
            </Route>

            <Route path="/affiliates" element={<DashboardLayout />}> 
              <Route 
                index 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <AffiliatesPage />
                  </React.Suspense>
                } 
              />
            </Route>

            <Route path="/users" element={<DashboardLayout />}>
              <Route 
                index 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <UsersPage />
                  </React.Suspense>
                } 
              />
            </Route>

            {/* Applications feature removed - approvals now handled in merchants table */}

            {/* Public routes */}
            <Route 
              path="/terms" 
              element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <TermsAndConditionsPage />
                </React.Suspense>
              } 
            />
            <Route 
              path="/privacy" 
              element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <PrivacyPolicyPage />
                </React.Suspense>
              } 
            />
            <Route 
              path="/partners/signup" 
              element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <MerchantSignup />
                </React.Suspense>
              } 
            />
            <Route 
              path="/partners/signup/processing" 
              element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <SignupProcessing />
                </React.Suspense>
              } 
            />
            <Route 
              path="/partners/signup/existing" 
              element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <MerchantSignupExisting />
                </React.Suspense>
              } 
            />
            <Route 
              path="/partners/convert" 
              element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <MerchantConversion />
                </React.Suspense>
              } 
            />
            <Route 
              path="/partners/success" 
              element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <MerchantSignupSuccess />
                </React.Suspense>
              } 
            />
            {/* Public affiliate landing route */}
            <Route 
              path="/affiliate" 
              element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <AffiliateLandingPage />
                </React.Suspense>
              } 
            />
            
            {/* Sign-in redirect for merchant signup */}
            <Route path="/sign-in" element={<Navigate to="/auth/login" replace />} />

            {/* Fallback: send unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </QueryProvider>
    </BrowserRouter>
  );
}

export default App;
